/**
 * WS-14 — IN-APP HELP (the help drawer).
 *
 * A proper modal dialog (role=dialog, aria-modal, the shared useFocusTrap, labelled by its heading)
 * slid in from the right. Static how-tos (./content.tsx) for the headline surfaces — diary/queue,
 * till, payments, stock, comms, reports — plus a link to the public roadmap/changelog and a support
 * mailto. No tour library, no chat widget. Opened from the "?" button in the AppShell; Esc closes and
 * focus returns to that button.
 */
import { useRef } from 'react';
import { Button, IconButton, useFocusTrap } from '@salonomy/ui';
import { env } from '../../lib/env.js';
import { HELP_TOPICS, PUBLIC_ROADMAP_PATH, SUPPORT_EMAIL } from './content.js';

const HELP_TITLE_ID = 'help-drawer-title';

export function HelpDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const panelRef = useRef<HTMLDivElement>(null);
  // Trap focus, close on Escape, restore focus to the opener (the "?" button) on close.
  useFocusTrap(panelRef, open, onClose);
  if (!open) return null;

  const roadmapUrl = `${env.queuePublicBaseUrl}${PUBLIC_ROADMAP_PATH}`;

  return (
    <div className="help-overlay" role="presentation" onMouseDown={onClose}>
      <aside
        ref={panelRef}
        className="help-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby={HELP_TITLE_ID}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className="help-drawer__head">
          <h2 id={HELP_TITLE_ID} className="help-drawer__title">
            Help &amp; how-tos
          </h2>
          <IconButton label="Close help" onClick={onClose} className="help-drawer__close">
            ×
          </IconButton>
        </header>

        <div className="help-drawer__body">
          {HELP_TOPICS.map((t) => (
            <section key={t.id} className="help-topic" aria-labelledby={`help-topic-${t.id}`}>
              <h3 id={`help-topic-${t.id}`} className="help-topic__title">
                {t.title}
              </h3>
              <div className="help-topic__body">{t.body}</div>
            </section>
          ))}
        </div>

        <footer className="help-drawer__foot">
          <a className="help-link" href={roadmapUrl} target="_blank" rel="noreferrer noopener">
            What's new &amp; on the roadmap ↗
          </a>
          <a className="help-link" href={`mailto:${SUPPORT_EMAIL}`}>
            Email support: {SUPPORT_EMAIL}
          </a>
          <Button variant="outline" size="sm" onClick={onClose} block>
            Close
          </Button>
        </footer>
      </aside>
    </div>
  );
}
